import { InternalServerErrorMessage, getErrorMessage } from "@/libs/errors";
import { ApolloError } from "@apollo/client";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { useRollbar } from "@rollbar/react";

/**
 * Function to get the error handler for GraphQL errors.This function displays an error message to
 * the user based on the error code from the GraphQL API's error response, using the mapping information
 * of error codes and error messages prepared on the frontend side. In case of an unanticipated system
 * error that cannot be retrieved from the mapping information, it displays the defaultErrorMessage to
 * the user.
 *
 * @param {string} _errorMessageToRollbar - Kept for compatibility with existing callers.
 * @param {string} [defalutErrorMessage=InternalServerErrorMessage] - The default error message to be
 *  used in case of uncaught exceptions or system errors that are not anticipated.
 * @returns {(err: ApolloError) => void} - A function that takes an ApolloError and handles it.
 */
export type getNotifyErrorHandlerForGraphQL = (
  _errorMessageToRollbar: string,
  defalutErrorMessage?: string
) => (err: ApolloError) => void;

/**
 * Hook to use Rollbar utility.
 * @returns {{rollbar: Rollbar, getNotifyErrorHandlerForGraphQL: getNotifyErrorHandlerForGraphQL}} - An object containing the Rollbar instance and the error handler function for GraphQL errors.
 * @property {Rollbar} rollbar - The Rollbar instance.
 * @property {getNotifyErrorHandlerForGraphQL} getNotifyErrorHandlerForGraphQL - The error handler function for GraphQL errors.
 */
const useRollbarUtil = () => {
  const rollbar = useRollbar();
  const { notify } = useSnackBar();

  const getNotifyErrorHandlerForGraphQL: getNotifyErrorHandlerForGraphQL =
    (
      _errorMessageToRollbar: string,
      defalutErrorMessage: string = InternalServerErrorMessage
    ) =>
    (err: ApolloError) => {
      const errMessage = getErrorMessage(err, defalutErrorMessage);
      notify(errMessage);
    };
  return {
    rollbar,
    getNotifyErrorHandlerForGraphQL,
  };
};

export default useRollbarUtil;

export const DefaultRollbarContext = "Default Rollbar Context";
